import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import EventMediaGallery from '../components/EventMediaGallery';
import EventReviews from '../components/EventReviews';
import EventInfoCard from '../components/event-detail/EventInfoCard';
import EventCommentsSection from '../components/event-detail/EventCommentsSection';
import EventSidebar from '../components/event-detail/EventSidebar';
import { PAYMENTS_ENABLED } from '../config/features';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRSVPDialog, setShowRSVPDialog] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  const loadEvent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      toast.error('Error al cargar el evento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${id}/comments`);
      setComments(response.data);
    } catch {
      /* silent */
    }
  }, [id]);

  useEffect(() => {
    loadEvent();
    loadComments();
  }, [loadEvent, loadComments]);

  const proceedToMPPayment = async (rsvpId) => {
    const prefResp = await axios.post(
      `${API}/payments/preference/rsvp/${rsvpId}`,
      {},
      { withCredentials: true }
    );
    if (prefResp.data?.init_point) {
      toast.success('Redirigiendo a MercadoPago...');
      window.location.href = prefResp.data.init_point;
      return true;
    }
    return false;
  };

  const findMyPendingRSVPForEvent = async () => {
    try {
      const resp = await axios.get(`${API}/rsvps/my`, { withCredentials: true });
      return (resp.data || []).find(
        r => r.event_id === id && r.payment_status !== 'completed'
      );
    } catch {
      return null;
    }
  };

  const handleRSVP = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/eventos/${id}` } });
      return;
    }
    if (event.price > 0 && !PAYMENTS_ENABLED) {
      toast.info('La venta de entradas estará disponible próximamente.');
      return;
    }

    setRsvpLoading(true);
    try {
      let rsvpId;
      try {
        const rsvpResp = await axios.post(
          `${API}/events/${id}/rsvp`,
          { event_id: id, quantity: 1 },
          { withCredentials: true }
        );
        rsvpId = rsvpResp.data?.rsvp_id;
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        const alreadyRegistered = err.response?.status === 400 && detail.toLowerCase().includes('already registered');
        // Already registered → resume payment if event is paid and RSVP is still pending
        if (alreadyRegistered && event.price > 0) {
          const existing = await findMyPendingRSVPForEvent();
          if (existing) {
            rsvpId = existing.rsvp_id;
            toast.info('Retomando pago pendiente...');
          } else {
            toast.error('Ya estás anotado a este evento.');
            return;
          }
        } else if (alreadyRegistered) {
          toast.info('Ya estás anotado a este evento. Podés verlo en tu perfil.');
          return;
        } else {
          toast.error(detail || 'Error al anotarse');
          return;
        }
      }

      // Paid event → redirect to MP checkout
      if (event.price > 0 && rsvpId) {
        try {
          const ok = await proceedToMPPayment(rsvpId);
          if (ok) return;
        } catch (payErr) {
          toast.error('Reserva creada, pero el pago falló. Podés reintentarlo desde "Mis reservas".');
          setShowRSVPDialog(true);
          return;
        }
      }

      toast.success('¡Te anotaste exitosamente!');
      setShowRSVPDialog(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al anotarse';
      toast.error(message);
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      await axios.post(
        `${API}/events/${id}/comments`,
        { text: newComment },
        {
          withCredentials: true
        }
      );
      setNewComment('');
      loadComments();
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-[#525252] mb-4">Evento no encontrado</h2>
          <Link to="/explorar">
            <Button>Volver a explorar</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="event-detail-page">
      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-[#525252] hover:text-[#0A0A0A]"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>

      <div className="container mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative" data-testid="event-hero-media">
              <EventMediaGallery
                images={event.images || []}
                videoUrl={event.video_url || null}
                alt={event.title}
                variant="hero"
              />
              {event.is_solidarity && (
                <div className="absolute top-4 right-4 z-20 bg-primary text-white px-4 py-2 rounded-full font-semibold flex items-center gap-2 shadow-lg">
                  <Heart className="w-5 h-5" />
                  Evento Solidario
                </div>
              )}
            </div>

            <EventInfoCard event={event} />

            {/* Reviews */}
            <div className="bg-white/50 backdrop-blur-sm border border-[#E5E5E5] rounded-lg p-6">
              <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-400" />
                Reseñas
                {event.rating_count > 0 && (
                  <span className="text-sm font-normal text-[#737373]">
                    · {event.rating_avg?.toFixed(1)} de {event.rating_count}
                  </span>
                )}
              </h2>
              <EventReviews eventId={id} />
            </div>

            <EventCommentsSection
              comments={comments}
              user={user}
              newComment={newComment}
              setNewComment={setNewComment}
              onSubmit={handleComment}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <EventSidebar event={event} rsvpLoading={rsvpLoading} onRSVP={handleRSVP} />
          </div>
        </div>
      </div>

      {/* RSVP Success Dialog */}
      <Dialog open={showRSVPDialog} onOpenChange={setShowRSVPDialog}>
        <DialogContent className="bg-white border-[#E5E5E5] text-[#0A0A0A]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">¡Confirmado!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#525252]">
              Tu inscripción fue confirmada. Podés ver tus eventos en tu perfil.
            </p>
            <div className="flex gap-3">
              <Button onClick={() => setShowRSVPDialog(false)} variant="outline" className="flex-1">
                Cerrar
              </Button>
              <Link to="/perfil" className="flex-1">
                <Button className="w-full bg-primary hover:bg-primary/90">Ver mis eventos</Button>
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDetail;
